import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, UserCog, KeyRound } from "lucide-react";

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const blank = { name: "", email: "", password: "", role: "sales" };
  const [form, setForm] = useState(blank);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      await api.post("/users", form);
      toast.success("Akun dibuat");
      setOpen(false); setForm(blank); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Akun dihapus"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resetPw = async () => {
    try {
      await api.put(`/users/${pwTarget.id}/password`, { password: newPw });
      toast.success(`Password ${pwTarget.name} diperbarui`);
      setPwOpen(false); setNewPw("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader overline="Pengaturan" title="Tim & Akses">
        <Button data-testid="add-user-btn" onClick={() => { setForm(blank); setOpen(true); }} className="rounded-sm">
          <Plus className="w-4 h-4 mr-1" /> Tambah Akun
        </Button>
      </PageHeader>
      <div className="p-8">
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary">
              <tr className="text-left overline text-muted-foreground">
                <th className="px-4 py-3">Nama</th><th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Peran</th><th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-secondary/40" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-mono uppercase ${u.role === "admin" ? "bg-primary/12 text-primary" : "bg-secondary text-muted-foreground"}`}>
                      {u.role === "admin" ? <ShieldCheck className="w-3 h-3" /> : <UserCog className="w-3 h-3" />} {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => { setPwTarget(u); setNewPw(""); setPwOpen(true); }} className="text-muted-foreground hover:text-primary mr-3" data-testid={`reset-pw-${u.id}`} title="Reset password"><KeyRound className="w-4 h-4" /></button>
                    {u.id !== user?.id && (
                      <button onClick={() => del(u.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">Tambah Akun</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="overline text-muted-foreground">Nama</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
            <div><Label className="overline text-muted-foreground">Email</Label>
              <Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
            <div><Label className="overline text-muted-foreground">Password</Label>
              <Input data-testid="user-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
            <div><Label className="overline text-muted-foreground">Peran</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="mt-1 rounded-sm bg-secondary"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">Sales (akses terbatas)</SelectItem>
                  <SelectItem value="admin">Admin (akses penuh)</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          <DialogFooter><Button data-testid="save-user-btn" onClick={save} className="rounded-sm">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">Reset Password — {pwTarget?.name}</DialogTitle></DialogHeader>
          <div>
            <Label className="overline text-muted-foreground">Password Baru</Label>
            <Input data-testid="new-password-input" type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="mt-1 rounded-sm bg-secondary" />
          </div>
          <DialogFooter><Button data-testid="save-password-btn" onClick={resetPw} className="rounded-sm">Simpan Password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
